import {
	Card,
	CardContent,
	Typography,
	Box,
	alpha,
	LinearProgress,
	Grid,
} from "@mui/material";
import { Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { useTheme } from "@emotion/react";
import useStyles from "./styles";

const DonutChartComponent = ({
	data = [],
	title = "",
	loading = false,
	type = 0,
}) => {
	const theme = useTheme();
	const styles = useStyles(theme);

	const colors = [
		theme.palette.colors.green.light,
		theme.palette.colors.blue.medium,
		theme.palette.colors.orange.medium,
		theme.palette.colors.red.medium,
		theme.palette.colors.purple.medium,
		theme.palette.colors.pink.medium,
		theme.palette.colors.yellow.medium,
		theme.palette.colors.gray.medium,
		theme.palette.colors.blue.lighter,
		theme.palette.colors.orange.dark,
		theme.palette.colors.pink.light,
		theme.palette.colors.purple.dark,
	];

	let colorIndex = 0;

	const chartData = data?.map((item) => {
		const isNaoCadastrado = item?.name === "Não cadastrado";

		const autoColor = isNaoCadastrado
			? `${theme.palette.colors.gray.medium}95`
			: colors[colorIndex % colors?.length];

		if (!isNaoCadastrado) {
			colorIndex++;
		}

		return {
			...item,
			color: item?.color || autoColor,
			total: Number(item?.totalAtendimentos ?? item?.total ?? item?.value ?? 0),
		};
	});

	const legendData =
		type === 1 && chartData?.length > 0
			? [
				{
					label: "Abertos",
					value: chartData[0]?.abertos ?? 0,
					color: theme.palette.colors.blue.medium,
				},
				{
					label: "Resolvidos",
					value: chartData[0]?.fechados ?? 0,
					color: theme.palette.colors.green.light,
				},
			]
			: [];

	const CustomTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const item = payload[0];
			const valor = item?.value;
			const porcentagem = ((valor / totalGeral) * 100)?.toFixed(2);

			return (
				<Box sx={styles.containerTooltipDonut}>
					<Typography variant="body2" sx={{ fontWeight: 600 }}>
						{(item?.payload?.name || item?.name) &&
							`${item?.payload?.name || item?.name}: ${porcentagem}%`}
					</Typography>

					<Typography variant="body2">{`${valor} atendimentos`}</Typography>
				</Box>
			);
		}

		return null;
	};

	const totalGeral = chartData.reduce((sum, item) => sum + item?.total, 0);

	return (
		<Card sx={styles.containerCard}>
			{loading && <LinearProgress sx={styles.linearProgressLoading} />}
			<CardContent sx={styles.containerCard}>
				<Typography variant="h8" sx={{ fontWeight: 600 }}>
					{title}
				</Typography>
				<Box sx={styles.containerCard}>
					<Grid container sx={styles.containerCard}>
						{/* COLUNA DO GRÁFICO */}
						<Grid item xs={6} sx={styles.containerCard}>
							<ResponsiveContainer width="100%" height="100%">
								<PieChart>
									<Tooltip
										content={CustomTooltip}
										cursor={{
											fill:
												theme.palette.mode === "dark"
													? alpha(theme.palette.colors.black.medium, 0.4)
													: alpha(theme.palette.colors.black.light, 0.4),
										}}
									/>

									<Pie
										data={type === 1 ? legendData : chartData}
										dataKey={type === 1 ? "value" : "total"}
										nameKey={type === 1 ? "label" : "faixa_tempo"}
										cx="50%"
										cy="50%"
										innerRadius="70%"
										outerRadius="85%"
										paddingAngle={3}
										isAnimationActive={false}
										stroke="none"
										cornerRadius={20}
										minAngle={5}
									>
										{(type === 1 ? legendData : chartData)?.map(
											(entry, index) => (
												<Cell key={`cell-${index}`} fill={entry?.color} />
											)
										)}
									</Pie>

									<text
										x="50%"
										y="50%"
										textAnchor="middle"
										dominantBaseline="middle"
										style={{
											fill: theme.palette.text.primary,
											fontSize: 20,
											fontWeight: 600,
										}}
									>
										{totalGeral}
									</text>
									<text
										x="50%"
										y="65%"
										textAnchor="middle"
										style={{
											fontSize: 14,
											fill: theme.palette.colors.gray.dark,
										}}
									>
										Total
									</text>
								</PieChart>
							</ResponsiveContainer>
						</Grid>

						{/* COLUNA DA LEGENDA */}
						<Grid
							item
							xs={6}
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								gap: 1,

								...(title === "Atendimentos por regional" &&
									data?.length > 5 && {
									border: `1px solid ${theme.palette.colors.gray.medium}36`,
									borderRadius: 4,
									p: 1,
									mb: 1,
									height: "100%",
								}),
							}}
						>
							<Box
								sx={{
									...(title === "Atendimentos por regional" && {
										minHeight: 100,
										maxHeight: 150,
										overflowY: "auto",
										p: 0,
										mb: 0,
									}),
								}}
							>
								{type === 0
									? chartData?.map((item, index) => (
										<Box
											key={index}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												mb: 1,
											}}
										>
											<Box
												sx={{
													width: 4,
													height: 16,
													borderRadius: 999,
													backgroundColor: item.color,
												}}
											/>
											<Typography variant="body2">
												{item.faixa_tempo || item?.name} —{" "}
												<strong>{item.total}</strong>
											</Typography>
										</Box>
									))
									: legendData.map((item, index) => (
										<Box
											key={index}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											<Box
												sx={{
													width: 4,
													height: 18,
													borderRadius: 999,
													backgroundColor: item.color,
												}}
											/>
											<Typography variant="body2">
												{item?.label} — <strong>{item?.value}</strong>
											</Typography>
										</Box>
									))}
							</Box>
						</Grid>
					</Grid>
				</Box>
			</CardContent>
		</Card>
	);
};

export default DonutChartComponent;
